/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 * Licensed under the VIJAYA 1.0 License.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../theme/ThemeContext';
import APIHelper from '../../api/APIHelper';
import envConfig from '../../config/envConfig';
import { useAlertDialog } from '../common/AlertDialogContext';
import Paginator from '../common/Paginator';

const apiHelper = new APIHelper({
    apiUrl:       envConfig.backendApiUrl,
    tokenUrl:     envConfig.backendTokenUrl,
    clientId:     envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const HOOK_LABELS = {
    'record.created':         { label: 'on_record_created',          desc: 'Fires after a record is inserted into any table.' },
    'record.updated':         { label: 'on_record_updated',          desc: 'Fires after a record is modified in any table.' },
    'record.deleted':         { label: 'on_record_deleted',          desc: 'Fires after a record is deleted from any table.' },
    'record.before_created':  { label: 'before_record_created',      desc: 'Runs synchronously before a record is inserted. Call cancel("reason") inside the hook to block the insert.', isPre: true },
    'record.before_updated':  { label: 'before_record_updated',      desc: 'Runs synchronously before a record is updated. Call cancel("reason") inside the hook to block the update.', isPre: true },
    'record.before_deleted':  { label: 'before_record_deleted',      desc: 'Runs synchronously before a record is deleted. Call cancel("reason") inside the hook to block the delete.', isPre: true },
    'user.created':           { label: 'on_user_created',            desc: 'Fires after a new user registers in the application.' },
    'user.updated':           { label: 'on_user_updated',            desc: 'Fires after a user profile is updated.' },
    'user.deleted':           { label: 'on_user_deleted',            desc: 'Fires after a user is removed from the application.' },
    'user.login':             { label: 'on_user_login',              desc: 'Fires after a user logs in successfully.' },
    'user.login_failed':      { label: 'on_user_login_failed',       desc: 'Fires when a login attempt fails.' },
    'user.password_changed':  { label: 'on_user_password_changed',   desc: 'Fires after a user changes their password.' },
    'user.role_assigned':     { label: 'on_user_role_assigned',      desc: 'Fires when a role is assigned to a user.' },
    'user.role_revoked':      { label: 'on_user_role_revoked',       desc: 'Fires when a role is removed from a user.' },
    'user.group_assigned':    { label: 'on_user_group_assigned',     desc: 'Fires when a user is added to a group.' },
    'user.group_removed':     { label: 'on_user_group_removed',      desc: 'Fires when a user is removed from a group.' },
};

const CATEGORY_LABELS = {
    record: { title: 'Record Events', icon: 'bi-table' },
    user:   { title: 'User Events',   icon: 'bi-person-fill' },
};

// ── Inline code editor (styled textarea) ────────────────────────────────────
function CodeEditor({ value, onChange, errorLine, maxLines }) {
    const [isDark, setIsDark] = useState(false); // default: light

    const lines   = value.split('\n');
    const lineCount = lines.length;
    const hasLimit  = maxLines != null && maxLines !== -1;
    const overLimit = hasLimit && lineCount > maxLines;

    const dark = {
        wrap:        '#0d1117',
        gutterBg:    '#010409',
        gutterBorder:'#21262d',
        gutterColor: '#484f58',
        border:      '#21262d',
        text:        '#e6edf3',
        caret:       '#58a6ff',
    };
    const light = {
        wrap:        '#ffffff',
        gutterBg:    '#f6f8fa',
        gutterBorder:'#d0d7de',
        gutterColor: '#6e7781',
        border:      '#d0d7de',
        text:        '#24292f',
        caret:       '#0969da',
    };
    const p = isDark ? dark : light;

    return (
        <div>
            {/* Toolbar: theme toggle + line counter */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <button
                    type="button"
                    onClick={() => setIsDark(d => !d)}
                    style={{
                        background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
                        padding: '2px 10px', fontSize: 12, cursor: 'pointer', color: '#64748b',
                    }}
                >
                    <i className={`bi ${isDark ? 'bi-sun' : 'bi-moon'} me-1`}></i>
                    {isDark ? 'Light Mode' : 'Dark Mode'}
                </button>
                {hasLimit && (
                    <span style={{ fontSize: 11, fontFamily: 'monospace', color: overLimit ? '#991b1b' : '#94a3b8' }}>
                        {lineCount} / {maxLines} lines{overLimit ? ' — exceeds limit' : ''}
                    </span>
                )}
            </div>

            {/* Editor */}
            <div style={{
                display: 'flex', fontFamily: "'Fira Code', 'Courier New', monospace",
                fontSize: 13, lineHeight: '1.65', background: p.wrap, borderRadius: 8,
                overflow: 'hidden', border: `1px solid ${p.border}`,
            }}>
                {/* Gutter */}
                <div style={{
                    background: p.gutterBg, borderRight: `1px solid ${p.gutterBorder}`,
                    padding: '12px 0', minWidth: 44, textAlign: 'right',
                    userSelect: 'none', flexShrink: 0,
                }}>
                    {lines.map((_, i) => (
                        <div
                            key={i}
                            style={{
                                height: '1.65em', paddingRight: 10, fontSize: 12,
                                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                                color: errorLine && errorLine === i + 1 ? '#f85149' : p.gutterColor,
                                background: errorLine && errorLine === i + 1 ? 'rgba(248,81,73,0.08)' : 'transparent',
                            }}
                        >
                            {i + 1}
                        </div>
                    ))}
                </div>
                {/* Textarea */}
                <textarea
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    spellCheck={false}
                    style={{
                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                        color: p.text, padding: '12px 16px', resize: 'none',
                        lineHeight: '1.65', fontFamily: 'inherit', fontSize: 13,
                        minHeight: 280, caretColor: p.caret,
                    }}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                />
            </div>
        </div>
    );
}

// ── Logs modal ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 20;

function HookLogsModal({ applicationId, eventType, onClose }) {
    const { theme } = useTheme();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [expandedId, setExpandedId] = useState(null);

    // Applied filter (drives API) — only status for now
    const [appliedStatus, setAppliedStatus] = useState('');
    const [statusDraft, setStatusDraft] = useState('');

    const totalPages = Math.ceil(total / PAGE_SIZE);

    const fetchLogs = useCallback(async (p = 1) => {
        setLoading(true);
        setExpandedId(null);
        try {
            let url = `/arcee/hooks/${eventType}/logs/?application_id=${applicationId}&page=${p}&page_size=${PAGE_SIZE}`;
            if (appliedStatus) url += `&status=${appliedStatus}`;
            const resp = await apiHelper.apiCall(url, { method: 'GET' });
            if (resp.ok) {
                const data = await resp.json();
                setLogs(data.results || []);
                setTotal(data.count || 0);
                setPage(p);
            }
        } finally {
            setLoading(false);
        }
    }, [applicationId, eventType, appliedStatus]);

    useEffect(() => { fetchLogs(1); }, [fetchLogs]);

    const applyStatus = (val) => {
        setStatusDraft(val);
        setAppliedStatus(val);
    };

    const startRow = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const endRow   = Math.min(page * PAGE_SIZE, total);

    return (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1050 }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
                <div className="modal-content" style={{ borderRadius: theme.cardRadius || 12 }}>
                    {/* Header */}
                    <div className="modal-header border-0 pb-0">
                        <div>
                            <h5 className="modal-title fw-bold">
                                <i className="bi bi-clock-history me-2" style={{ color: theme.primary }}></i>
                                Execution Logs — <code style={{ fontSize: 15 }}>{HOOK_LABELS[eventType]?.label}</code>
                            </h5>
                            <p className="text-muted small mb-0">{total} total execution{total !== 1 ? 's' : ''}</p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {/* Status filter */}
                            <select
                                className="form-select form-select-sm"
                                style={{ fontSize: 12, width: 'auto' }}
                                value={statusDraft}
                                onChange={e => applyStatus(e.target.value)}
                            >
                                <option value="">All statuses</option>
                                <option value="success">Success</option>
                                <option value="failed">Failed</option>
                                <option value="timeout">Timeout</option>
                                <option value="cancelled">Cancelled (pre-save)</option>
                            </select>
                            <button className="btn btn-sm btn-light" onClick={() => fetchLogs(page)} title="Refresh">
                                <i className="bi bi-arrow-clockwise"></i>
                            </button>
                            <button className="btn-close" onClick={onClose} />
                        </div>
                    </div>

                    {/* Body */}
                    <div className="modal-body p-0">
                        {loading && (
                            <div className="text-center py-5">
                                <div className="spinner-border" style={{ color: theme.primary }} role="status" />
                            </div>
                        )}
                        {!loading && logs.length === 0 && (
                            <div className="text-center py-5 text-muted">
                                <i className="bi bi-journal-x fs-1 d-block mb-2 opacity-50"></i>
                                No executions found. {appliedStatus ? 'Try clearing the status filter.' : 'Enable the hook and trigger a record operation.'}
                            </div>
                        )}
                        {!loading && logs.length > 0 && (
                            <div className="table-responsive">
                                <table className="table mb-0" style={{ fontSize: 13 }}>
                                    <thead style={{ background: '#f8fafc' }}>
                                        <tr>
                                            <th className="px-4 py-3 fw-semibold text-muted">Date</th>
                                            <th className="py-3 fw-semibold text-muted">Status</th>
                                            <th className="py-3 fw-semibold text-muted">Source</th>
                                            <th className="py-3 fw-semibold text-muted">Table</th>
                                            <th className="py-3 fw-semibold text-muted">Records</th>
                                            <th className="py-3 fw-semibold text-muted">Duration</th>
                                            <th className="py-3"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {logs.map(log => {
                                            const isExpanded = expandedId === log.id;
                                            const hasDetails = !!(log.error_traceback || log.log_output || log.stdout);
                                            const isSuccess   = log.status === 'success';
                                            const isTimeout   = log.status === 'timeout';
                                            const isCancelled = log.status === 'cancelled';
                                            let badgeBg, badgeColor, badgeIcon;
                                            if (isSuccess) {
                                                badgeBg = '#dcfce7'; badgeColor = '#166534'; badgeIcon = 'bi-check-circle-fill';
                                            } else if (isTimeout) {
                                                badgeBg = '#fef3c7'; badgeColor = '#92400e'; badgeIcon = 'bi-clock-fill';
                                            } else if (isCancelled) {
                                                badgeBg = '#fdf2f8'; badgeColor = '#9f1239'; badgeIcon = 'bi-shield-fill-x';
                                            } else {
                                                badgeBg = '#fee2e2'; badgeColor = '#991b1b'; badgeIcon = 'bi-x-circle-fill';
                                            }
                                            return (
                                                <React.Fragment key={log.id}>
                                                    <tr
                                                        style={{ cursor: hasDetails ? 'pointer' : 'default', background: isExpanded ? '#f8fafc' : undefined }}
                                                        onClick={() => hasDetails && setExpandedId(isExpanded ? null : log.id)}
                                                    >
                                                        <td className="px-4 py-3 text-muted" style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                                                            {log.finished_at ? new Date(log.finished_at).toLocaleString() : '—'}
                                                        </td>
                                                        <td className="py-3">
                                                            <span style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
                                                                background: badgeBg, color: badgeColor,
                                                            }}>
                                                                <i className={`bi ${badgeIcon}`}></i>
                                                                {log.status}
                                                            </span>
                                                        </td>
                                                        <td className="py-3">
                                                            <span style={{
                                                                fontSize: 11, fontWeight: 600,
                                                                color: log.was_override ? '#b45309' : '#1d4ed8',
                                                            }}>
                                                                {log.was_override ? 'Custom' : 'Default'}
                                                            </span>
                                                        </td>
                                                        <td className="py-3">
                                                            <code style={{ fontSize: 12 }}>{log.trigger_context?.table_name || '—'}</code>
                                                        </td>
                                                        <td className="py-3 text-muted" style={{ fontSize: 12 }}>
                                                            {log.trigger_context?.record_ids?.length ?? '—'}
                                                        </td>
                                                        <td className="py-3 text-muted" style={{ fontSize: 12 }}>
                                                            {log.execution_time_ms != null ? `${log.execution_time_ms} ms` : '—'}
                                                        </td>
                                                        <td className="py-3 text-end pe-3">
                                                            {hasDetails && (
                                                                <i className={`bi ${isExpanded ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`} style={{ fontSize: 13 }}></i>
                                                            )}
                                                        </td>
                                                    </tr>
                                                    {isExpanded && (
                                                        <tr>
                                                            <td colSpan={7} style={{ padding: '12px 24px 16px', background: '#fafafa', borderBottom: '1px solid #e2e8f0' }}>
                                                                {log.error_traceback && (
                                                                    <div className="mb-3">
                                                                        <div className="fw-bold mb-1" style={{ color: '#991b1b', fontSize: 12 }}>Traceback</div>
                                                                        <pre style={{ background: '#fff1f2', border: '1px solid #fecaca', borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 12, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{log.error_traceback}</pre>
                                                                    </div>
                                                                )}
                                                                {log.log_output && (
                                                                    <div className="mb-3">
                                                                        <div className="fw-bold mb-1" style={{ color: '#475569', fontSize: 12 }}>Logger Output</div>
                                                                        <pre style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 14px', fontSize: 12, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{log.log_output}</pre>
                                                                    </div>
                                                                )}
                                                                {log.stdout && (
                                                                    <div>
                                                                        <div className="fw-bold mb-1" style={{ color: '#475569', fontSize: 12 }}>stdout (print)</div>
                                                                        <pre style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 14px', fontSize: 12, margin: 0, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{log.stdout}</pre>
                                                                    </div>
                                                                )}
                                                                {!log.error_traceback && !log.log_output && !log.stdout && (
                                                                    <span className="text-muted" style={{ fontSize: 12 }}>No output captured.</span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Footer — always visible count + Paginator */}
                    <div className="modal-footer border-0 flex-column align-items-center gap-1 pt-2">
                        <small className="text-muted" style={{ fontSize: 12 }}>
                            {total === 0
                                ? 'No executions found'
                                : `Showing ${startRow}–${endRow} of ${total} execution${total !== 1 ? 's' : ''}`}
                        </small>
                        <Paginator
                            page={page}
                            pageCount={totalPages}
                            onPrevious={p => fetchLogs(p)}
                            onPage={p => fetchLogs(p)}
                            onNext={p => fetchLogs(p)}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Hook editor panel ────────────────────────────────────────────────────────
function HookEditor({ hook, applicationId, onSaved, onClose, maxLines = -1 }) {
    const [code, setCode] = useState(hook.code || '');
    const [saving, setSaving] = useState(false);
    const [validating, setValidating] = useState(false);
    const [testing, setTesting] = useState(false);
    const [validation, setValidation] = useState(null); // {ok, msg, line}
    const [testResult, setTestResult] = useState(null);
    const { showAlertDialog } = useAlertDialog();

    const errorLine = validation && !validation.ok ? validation.line : null;

    const handleValidate = async () => {
        setValidating(true);
        setTestResult(null);
        try {
            const resp = await apiHelper.apiCall(`/arcee/hooks/${hook.event_type}/validate/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code }),
            });
            const data = await resp.json();
            if (data.valid) {
                setValidation({ ok: true, msg: data.message });
            } else {
                setValidation({ ok: false, msg: data.error, line: data.line });
            }
        } finally {
            setValidating(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setTestResult(null);
        try {
            const resp = await apiHelper.apiCall(`/arcee/hooks/${hook.event_type}/`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId), code }),
            });
            const data = await resp.json();
            if (resp.ok) {
                setValidation({ ok: true, msg: 'Saved successfully.' });
                onSaved(data);
            } else {
                setValidation({ ok: false, msg: data.error || data.detail || 'Save failed.', line: data.line });
            }
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => {
        showAlertDialog({
            title: 'Reset to Default',
            message: 'This will remove your custom code and revert to the default (log-only) implementation. The hook will still be enabled/disabled as-is. Continue?',
            type: 'warning',
            showCancel: true,
            onOk: async () => {
                try {
                    const resp = await apiHelper.apiCall(`/arcee/hooks/${hook.event_type}/?application_id=${applicationId}`, { method: 'DELETE' });
                    if (resp.ok) {
                        const data = await resp.json();
                        setCode(data.code || '');
                        setValidation(null);
                        setTestResult(null);
                        onSaved(data);
                    } else {
                        const data = await resp.json().catch(() => ({}));
                        setValidation({ ok: false, msg: data.error || 'Reset failed. Please try again.' });
                    }
                } catch (err) {
                    setValidation({ ok: false, msg: `Network error: ${err.message || 'Could not reach the server.'}` });
                }
            },
        });
    };

    const _executeTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const resp = await apiHelper.apiCall(`/arcee/hooks/${hook.event_type}/test/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId), code }),
            });
            const data = await resp.json();
            setTestResult(data);
        } catch (err) {
            setTestResult({
                status: 'failed',
                stdout: '',
                log_output: '',
                error_traceback: `Network error: ${err.message || 'Could not reach the server. Please check your connection and try again.'}`,
                execution_time_ms: null,
            });
        } finally {
            setTesting(false);
        }
    };

    const lineCount    = code.split('\n').length;
    const overLineLimit = maxLines !== -1 && lineCount > maxLines;
    const canSave = !saving && code.trim() !== '' && !overLineLimit;
    const label   = HOOK_LABELS[hook.event_type];

    return (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
            {/* Editor titlebar */}
            <div style={{ background: '#1e293b', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#c084fc', fontWeight: 600 }}>{label?.label}(context)</span>
                    <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                        background: hook.has_override ? '#422006' : '#1e3a5f',
                        color: hook.has_override ? '#fbbf24' : '#60a5fa',
                    }}>
                        {hook.has_override ? '● Custom Override' : '◎ Default'}
                    </span>
                </div>
                <button className="btn btn-sm" style={{ background: 'transparent', color: '#94a3b8', border: '1px solid #334155', fontSize: 12 }} onClick={onClose}>
                    <i className="bi bi-x-lg"></i>
                </button>
            </div>

            {/* Code area */}
            <div style={{ padding: 16 }}>
                <CodeEditor
                    value={code}
                    onChange={v => { setCode(v); setValidation(null); }}
                    errorLine={errorLine}
                    maxLines={maxLines}
                />

                {/* Validation / status bar */}
                {validation && (
                    <div style={{
                        marginTop: 8, padding: '9px 14px', borderRadius: 6,
                        background: validation.ok ? '#f0fdf4' : '#fff1f2',
                        border: `1px solid ${validation.ok ? '#bbf7d0' : '#fecaca'}`,
                        color: validation.ok ? '#166534' : '#991b1b',
                        fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                        <i className={`bi ${validation.ok ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'}`}></i>
                        <span>
                            {validation.ok ? validation.msg : (
                                validation.line ? (
                                    <>
                                        <strong>SyntaxError</strong>
                                        {' at line '}<strong>{validation.line}</strong>
                                        {' — '}{validation.msg}
                                    </>
                                ) : validation.msg
                            )}
                        </span>
                    </div>
                )}

                {/* Test result */}
                {testResult && (() => {
                    const isCancelled = testResult.status === 'cancelled';
                    const isSuccessResult = testResult.status === 'success';
                    let bannerBg, bannerColor, bannerIcon;
                    if (isSuccessResult) {
                        bannerBg = '#f0fdf4'; bannerColor = '#166534'; bannerIcon = 'bi-play-circle-fill';
                    } else if (isCancelled) {
                        bannerBg = '#fdf2f8'; bannerColor = '#9f1239'; bannerIcon = 'bi-shield-fill-x';
                    } else {
                        bannerBg = '#fff1f2'; bannerColor = '#991b1b'; bannerIcon = 'bi-x-circle-fill';
                    }
                    return (
                        <div style={{ marginTop: 10, border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                            <div style={{ background: bannerBg, padding: '8px 14px', fontWeight: 700, fontSize: 12, color: bannerColor, display: 'flex', justifyContent: 'space-between' }}>
                                <span><i className={`bi ${bannerIcon} me-2`}></i>Test Run — {testResult.status} ({testResult.execution_time_ms != null ? `${testResult.execution_time_ms}ms` : 'N/A'})</span>
                                <button onClick={() => setTestResult(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
                            </div>
                            {isCancelled && testResult.cancel_reason && (
                                <div style={{ background: '#fdf2f8', padding: '8px 14px', fontSize: 12, color: '#9f1239', borderTop: '1px solid #fbcfe8' }}>
                                    <strong>cancel():</strong> {testResult.cancel_reason}
                                    <div style={{ marginTop: 4, color: '#831843', fontSize: 11 }}>
                                        In production, this would return <code>HTTP 400</code> to the client and the DB write would NOT happen.
                                    </div>
                                </div>
                            )}
                            {testResult.error_traceback && <pre style={{ background: '#fff1f2', color: '#991b1b', padding: '10px 14px', margin: 0, fontSize: 12, overflowX: 'auto', whiteSpace: 'pre-wrap' }}>{testResult.error_traceback}</pre>}
                            {testResult.log_output && <pre style={{ background: '#f8fafc', padding: '10px 14px', margin: 0, fontSize: 12, overflowX: 'auto', borderTop: '1px solid #e2e8f0', whiteSpace: 'pre-wrap' }}>{testResult.log_output}</pre>}
                            {testResult.stdout && <pre style={{ background: '#f8fafc', padding: '10px 14px', margin: 0, fontSize: 12, overflowX: 'auto', borderTop: '1px solid #e2e8f0', whiteSpace: 'pre-wrap' }}>{testResult.stdout}</pre>}
                        </div>
                    );
                })()}

                {/* Action buttons — order: Validate → Run Test → Save Override */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14 }}>
                    <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleReset}
                        disabled={!hook.has_override}
                    >
                        <i className="bi bi-arrow-counterclockwise me-1"></i>Reset to Default
                    </button>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button
                            className="btn btn-sm"
                            style={{ background: validation?.ok ? '#14532d' : '#1e293b', color: validation?.ok ? '#86efac' : '#94a3b8', border: '1px solid #33415544' }}
                            onClick={handleValidate}
                            disabled={validating || !code.trim()}
                        >
                            {validating ? <><span className="spinner-border spinner-border-sm me-1"></span>Checking...</> : <><i className="bi bi-check2 me-1"></i>Validate</>}
                        </button>
                        <button
                            className="btn btn-sm"
                            style={{ background: '#1e3a5f', color: '#93c5fd', border: '1px solid #2563eb44' }}
                            onClick={_executeTest}
                            disabled={testing || !code.trim() || hook.has_override || code !== hook.code}
                            title={hook.has_override || code !== hook.code ? 'Run Test is only available for the default (unmodified) hook code' : 'Run a test execution with sample data'}
                        >
                            {testing ? <><span className="spinner-border spinner-border-sm me-1"></span>Running...</> : <><i className="bi bi-play-fill me-1"></i>Run Test</>}
                        </button>
                        <button
                            className="btn btn-sm btn-primary"
                            onClick={handleSave}
                            disabled={!canSave}
                        >
                            {saving ? <><span className="spinner-border spinner-border-sm me-1"></span>Saving...</> : <><i className="bi bi-floppy me-1"></i>Save Override</>}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Main tab component ───────────────────────────────────────────────────────
export default function RecordHooksTab({ applicationId }) {
    const { theme } = useTheme();
    const { showAlertDialog } = useAlertDialog();
    const [hooks, setHooks] = useState([]);
    const [maxLines, setMaxLines] = useState(-1); // read from backend; -1 until loaded
    const [loading, setLoading] = useState(true);
    const [editingEventType, setEditingEventType] = useState(null);
    const [togglingType, setTogglingType] = useState(null);
    const [logsEventType, setLogsEventType] = useState(null);
    const [infoExpanded, setInfoExpanded] = useState(false);

    const fetchHooks = useCallback(async () => {
        setLoading(true);
        try {
            const resp = await apiHelper.apiCall(`/arcee/hooks/?application_id=${applicationId}`, { method: 'GET' });
            if (resp.ok) {
                const data = await resp.json();
                setHooks(data.results || []);
                setMaxLines(data.max_lines ?? -1);
            }
        } finally {
            setLoading(false);
        }
    }, [applicationId]);

    useEffect(() => { fetchHooks(); }, [fetchHooks]);

    const handleToggle = async (hook) => {
        setTogglingType(hook.event_type);
        try {
            const resp = await apiHelper.apiCall(`/arcee/hooks/${hook.event_type}/toggle/`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: parseInt(applicationId) }),
            });
            if (resp.ok) {
                const updated = await resp.json();
                setHooks(prev => {
                    const exists = prev.some(h => h.event_type === updated.event_type);
                    return exists
                        ? prev.map(h => h.event_type === updated.event_type ? updated : h)
                        : [...prev, updated];
                });
            }
        } finally {
            setTogglingType(null);
        }
    };

    const handleSaved = (updated) => {
        setHooks(prev => prev.map(h => h.event_type === updated.event_type ? updated : h));
    };

    const editingHook = hooks.find(h => h.event_type === editingEventType);

    if (loading) {
        return (
            <div className="text-center py-5 text-muted">
                <div className="spinner-border spinner-border-sm me-2"></div>Loading hooks...
            </div>
        );
    }

    const hooksByCategory = hooks.reduce((acc, hook) => {
        const category = hook.event_type.split('.')[0];
        if (!acc[category]) acc[category] = [];
        acc[category].push(hook);
        return acc;
    }, {});

    const categoryOrder = ['record', 'user'];
    const discoveredCategories = Object.keys(hooksByCategory).filter(c => !categoryOrder.includes(c));
    const sortedCategories = [...categoryOrder, ...discoveredCategories];

    const discoveredEventTypes = new Set(hooks.map(h => h.event_type));
    const availableToAdd = Object.entries(HOOK_LABELS).reduce((acc, [eventType, meta]) => {
        if (discoveredEventTypes.has(eventType)) return acc;
        const category = eventType.split('.')[0];
        if (!acc[category]) acc[category] = [];
        acc[category].push({ eventType, ...meta });
        return acc;
    }, {});

    return (
        <div>
            <div className="mb-4">
                <h5 className="fw-bold mb-1">Event Hooks</h5>
                <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                    Run Python code automatically when record or user events occur.
                    Hooks are <strong>off by default</strong> — toggle to enable.
                    Hooks appear here automatically once the event fires for the first time.
                </p>
            </div>

            {sortedCategories.map(category => {
                const categoryMeta = CATEGORY_LABELS[category] || { title: category, icon: 'bi-braces' };
                const categoryHooks = hooksByCategory[category] || [];
                const addableHooks  = availableToAdd[category] || [];
                return (
                    <div key={category} className="mb-4">
                        <div className="d-flex align-items-center gap-2 mb-3">
                            <i className={`bi ${categoryMeta.icon} text-primary`}></i>
                            <span className="fw-bold text-secondary" style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                {categoryMeta.title}
                            </span>
                            <div style={{ flex: 1, height: 1, background: '#e2e8f0' }}></div>
                            {addableHooks.length > 0 && (
                                <div className="dropdown">
                                    <button className="btn btn-sm btn-outline-primary dropdown-toggle" data-bs-toggle="dropdown" style={{ fontSize: 12 }}>
                                        <i className="bi bi-plus-lg me-1"></i>Add Hook
                                    </button>
                                    <ul className="dropdown-menu dropdown-menu-end shadow-sm" style={{ fontSize: 13, minWidth: 220 }}>
                                        {addableHooks.map(({ eventType, label }) => (
                                            <li key={eventType}>
                                                <button
                                                    className="dropdown-item d-flex flex-column py-2"
                                                    onClick={() => handleToggle({ event_type: eventType, is_active: false })}
                                                >
                                                    <code style={{ fontSize: 12 }}>{label}</code>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {categoryHooks.length === 0 && (
                            <div className="text-muted border rounded-3 bg-white text-center py-4" style={{ fontSize: 13 }}>
                                <i className="bi bi-clock-history d-block mb-1 opacity-50"></i>
                                No hooks active yet — use <strong>Add Hook</strong> to configure one, or wait for an event to fire.
                            </div>
                        )}

                        <div className="d-flex flex-column gap-3">
                            {categoryHooks.map(hook => {
                                const label     = HOOK_LABELS[hook.event_type];
                                const isEditing = editingEventType === hook.event_type;
                                const isToggling = togglingType === hook.event_type;
                                return (
                                    <div key={hook.event_type}>
                                        <div
                                            className="bg-white border rounded-3 px-4 py-3 d-flex align-items-center justify-content-between shadow-sm"
                                            style={{ borderColor: isEditing ? theme.primary + '80' : undefined }}
                                        >
                                            <div style={{ flex: 1 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
                                                    <span style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 14, color: '#1e293b' }}>
                                                        {label?.label}
                                                    </span>
                                                    {label?.isPre && (
                                                        <span
                                                            title="Synchronous pre-save hook — runs before the DB write and can veto it via cancel(reason)."
                                                            style={{
                                                                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                                                background: '#fdf2f8', color: '#9f1239', border: '1px solid #fbcfe8',
                                                            }}
                                                        >
                                                            <i className="bi bi-shield-fill-exclamation me-1"></i>Pre-save
                                                        </span>
                                                    )}
                                                    <span style={{
                                                        fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                                        background: hook.has_override ? '#fffbeb' : '#eff6ff',
                                                        color: hook.has_override ? '#b45309' : '#1d4ed8',
                                                        border: `1px solid ${hook.has_override ? '#fcd34d' : '#bfdbfe'}`,
                                                    }}>
                                                        {hook.has_override ? 'Custom' : 'Default'}
                                                    </span>
                                                    {hook.is_active && (
                                                        <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                                                            Active
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 13, color: '#64748b' }}>{label?.desc}</div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <span style={{ fontSize: 12, color: '#94a3b8' }}>{hook.is_active ? 'ON' : 'OFF'}</span>
                                                    <div
                                                        onClick={() => !isToggling && handleToggle(hook)}
                                                        style={{
                                                            width: 44, height: 24, borderRadius: 12,
                                                            background: hook.is_active ? theme.primary : '#cbd5e1',
                                                            position: 'relative', cursor: isToggling ? 'wait' : 'pointer',
                                                            transition: 'background 0.2s', opacity: isToggling ? 0.6 : 1,
                                                        }}
                                                    >
                                                        <div style={{
                                                            position: 'absolute', top: 3,
                                                            left: hook.is_active ? 23 : 3,
                                                            width: 18, height: 18, borderRadius: '50%',
                                                            background: '#fff', transition: 'left 0.2s',
                                                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                        }}></div>
                                                    </div>
                                                </div>

                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        background: logsEventType === hook.event_type ? '#1e3a5f' : '#f1f5f9',
                                                        color: logsEventType === hook.event_type ? '#93c5fd' : '#475569',
                                                        border: 'none',
                                                    }}
                                                    onClick={() => setLogsEventType(logsEventType === hook.event_type ? null : hook.event_type)}
                                                >
                                                    <i className="bi bi-clock-history me-1"></i>Logs
                                                </button>

                                                <button
                                                    className="btn btn-sm"
                                                    style={{
                                                        background: isEditing ? theme.primary : '#f1f5f9',
                                                        color: isEditing ? '#fff' : '#475569',
                                                        border: 'none',
                                                    }}
                                                    onClick={() => setEditingEventType(isEditing ? null : hook.event_type)}
                                                >
                                                    <i className={`bi ${isEditing ? 'bi-chevron-up' : 'bi-code-slash'} me-1`}></i>
                                                    {isEditing ? 'Close' : 'Edit Code'}
                                                </button>
                                            </div>
                                        </div>

                                        {isEditing && editingHook && (
                                            <div style={{ marginTop: 8 }}>
                                                <HookEditor
                                                    hook={editingHook}
                                                    applicationId={applicationId}
                                                    onSaved={handleSaved}
                                                    onClose={() => setEditingEventType(null)}
                                                    maxLines={maxLines}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {/* Info block — expandable */}
            <div className="alert alert-light border" style={{ fontSize: 13 }}>
                <div className="d-flex gap-3 align-items-start">
                    <i className="bi bi-info-circle-fill text-primary mt-1 flex-shrink-0"></i>
                    <div style={{ flex: 1 }}>
                        <div className="d-flex justify-content-between align-items-start gap-2">
                            <div>
                                <strong>Available inside every hook:</strong>{' '}
                                <code>context</code>, <code>ApplicationObject</code>, <code>logger</code>,{' '}
                                <code>requests</code>, <code>json</code>, <code>re</code>, <code>datetime</code>, <code>cancel</code>,{' '}
                                <code>send_email(to, subject, body, html=None)</code> (queued via platform SMTP; only logged on Test runs),{' '}
                                <code>send_template_email(template, to, values)</code> (sends a notification template, by name, to email addresses with the app's branded email layout; only logged on Test runs).
                                Post-save hooks (<code>record.created</code>, <code>user.*</code> etc.) run <strong>asynchronously</strong> — a crash does not roll back the change.
                                Pre-save hooks (<code>record.before_created/updated/deleted</code>) run <strong>synchronously before</strong> the DB write — call <code>cancel("reason")</code> to block the operation.
                            </div>
                            <button
                                type="button"
                                onClick={() => setInfoExpanded(e => !e)}
                                style={{ background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: 12, whiteSpace: 'nowrap', padding: 0, flexShrink: 0 }}
                            >
                                {infoExpanded ? 'Show less ▲' : 'Show more ▼'}
                            </button>
                        </div>

                        {infoExpanded && (
                            <div style={{ marginTop: 12, borderTop: '1px solid #dee2e6', paddingTop: 12 }}>
                                <div className="row g-3" style={{ fontSize: 12 }}>
                                    <div className="col-12 col-md-6">
                                        <strong>context</strong>
                                        <div className="text-muted mt-1">Dict of event data passed by the dispatcher. Keys vary by event — e.g. <code>table_name</code>, <code>record_ids</code>, <code>user_id</code>, <code>email</code>, <code>updated_fields</code>. Pre-save record events also include <code>payload</code> (the data about to be written) and, for update/delete, <code>before_data</code> (current values).</div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <strong>cancel(reason)</strong> <span style={{ background: '#fdf2f8', color: '#9f1239', padding: '1px 6px', borderRadius: 8, fontSize: 10, fontWeight: 700, marginLeft: 4 }}>Pre-save only</span>
                                        <div className="text-muted mt-1">
                                            Call inside a <code>record.before_*</code> hook to block the DB write. The client receives <code>HTTP 400</code> with the supplied reason. Example:
                                            <pre style={{ background: '#fff', border: '1px solid #fbcfe8', borderRadius: 4, padding: '6px 10px', margin: '6px 0 0', fontSize: 11 }}>
                                                {`payload = context.get("payload", {})
                                                if payload.get("amount", 0) <= 0:
                                                    cancel("Amount must be positive.")`}
                                            </pre>
                                            On post-save hooks, calling <code>cancel()</code> only marks the hook as cancelled in logs — the write has already happened.
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <strong>ApplicationObject</strong>
                                        <ul className="mb-0 ps-3 text-muted mt-1">
                                            <li><code>find(table, id)</code> — fetch one record by ID</li>
                                            <li><code>find_where(table, filters)</code> — filter records by field values</li>
                                            <li><code>find_all(table, offset, limit)</code> — paginated list</li>
                                            <li><code>create(table, data)</code> — insert a new record</li>
                                            <li><code>update(table, id, data)</code> — update an existing record</li>
                                            <li><code>delete(table, id)</code> — delete a record</li>
                                        </ul>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <strong>logger &amp; print</strong>
                                        <div className="text-muted mt-1">
                                            <code>logger.info()</code>, <code>.warning()</code>, <code>.error()</code>, <code>.debug()</code> — output captured and shown in Logs.<br />
                                            <code>print()</code> is also captured and shown under <em>stdout</em> in Logs.
                                        </div>
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <strong>requests</strong>
                                        <div className="text-muted mt-1">HTTP client for outbound calls. A <strong>30-second timeout</strong> is enforced on all requests to prevent hooks from hanging indefinitely.</div>
                                    </div>
                                    <div className="col-12">
                                        <strong>Restrictions</strong>
                                        <div className="text-muted mt-1">
                                            <code>os</code>, <code>subprocess</code>, <code>sys</code>, <code>open()</code> and other system-level modules are blocked.
                                            Hooks have a configurable execution time limit (default <strong>20 seconds</strong>) — any hook running beyond that is automatically terminated.
                                            Code length is limited to <strong>{maxLines === -1 ? 'unlimited' : `${maxLines} lines`}</strong> per hook.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {logsEventType && (
                <HookLogsModal
                    applicationId={applicationId}
                    eventType={logsEventType}
                    onClose={() => setLogsEventType(null)}
                />
            )}
        </div>
    );
}
